import React from 'react';
import { ThemeProvider } from 'styled-components';
import { changeStyleInTag } from 'core/utils/domUtils';
import { DesignSystemContext, DesignSystemContextProvider, Direction } from '@drone-js/core';
import { useSelector } from 'react-redux';
import { selectExperimentsFromBootstrap, selectLocaleFromBootstrap, selectMobileDeviceTypeFromBootstrap, selectRtlFromBootstrap } from 'store/slice/bootstrap';
import { mapDesignSystemExperiments } from 'app/DesignSystemExperiments';
import { Locale } from '@drone-js/core/src/contexts/session/Locale';
import { whitelabelThemeProvider } from '@drone-js/whitelabels';
import { whitelabelThemeProvider as kiteWhitelabelThemeProvider } from '@kite-js/whitelabel';
import { MobileDeviceType } from 'core/type/BootstrapConfig.types';
import { PlatformContext } from '@drone-js/core/src/contexts/session/Platform';

const deviceMapper: Partial<Record<MobileDeviceType, PlatformContext['platform']>> = {
    [MobileDeviceType.iOS]: 'ios',
    [MobileDeviceType.Android]: 'android',
};

export const WhiteLabelThemeProvider: React.FC<React.PropsWithChildren<{ whiteLabelId: number }>> = (props) => {
    const theme = React.useMemo(() => kiteWhitelabelThemeProvider(props.whiteLabelId), [props.whiteLabelId]);

    const experiments = useSelector(selectExperimentsFromBootstrap);
    const deviceType = useSelector(selectMobileDeviceTypeFromBootstrap);
    const locale = useSelector(selectLocaleFromBootstrap) as Locale | undefined;
    const isRtl = useSelector(selectRtlFromBootstrap);

    const droneCtx = React.useMemo<DesignSystemContext>(
        () => ({
            theme: whitelabelThemeProvider(props.whiteLabelId),
            direction: isRtl ? Direction.RTL : Direction.LTR,
            experiment: mapDesignSystemExperiments(experiments),
            locale,
            platform: deviceMapper[deviceType] || 'web',
        }),
        [props.whiteLabelId, experiments, locale, isRtl, deviceType],
    );

    React.useEffect(() => {
        changeStyleInTag('html', { backgroundColor: theme.palette.background[20] });
    }, [theme]);

    return (
        <ThemeProvider theme={theme}>
            <DesignSystemContextProvider value={droneCtx}>{props.children}</DesignSystemContextProvider>
        </ThemeProvider>
    );
};
WhiteLabelThemeProvider.displayName = 'WhiteLabelThemeProvider';
